import { appendImageRepoDomainIfNotExistInUrl } from '@/core/utils/helpers';

import { Thread } from '../types';

/**
 * Format thread display name based on thread type and participants
 */
export function formatThreadDisplayName(thread: Thread): string {
  if (thread.threadName) {
    return thread.threadName;
  }

  if (thread.vendor?.businessName) {
    return thread.vendor.businessName;
  }

  if (thread.users && thread.users.length > 0) {
    const names = thread.users
      .slice(0, 3)
      .map((user) => user.Name || user.businessName || 'Unknown')
      .join(', ');

    if (thread.users.length > 3) {
      return `${names} +${thread.users.length - 3}`;
    }

    return names;
  }

  return 'Unnamed Thread';
}

/**
 * Get thread avatar based on thread type
 */
export function getThreadAvatar(thread: Thread): string | null {
  if (thread.vendor?.businessLogo) {
    return appendImageRepoDomainIfNotExistInUrl(thread.vendor.businessLogo);
  }

  if (thread.vendor?.businessProfileImage) {
    return appendImageRepoDomainIfNotExistInUrl(thread.vendor.businessProfileImage);
  }

  if (thread.users && thread.users.length === 1) {
    const user = thread.users[0];
    const avatar = user.ProfileImage || user.businessProfileImage || null;
    return avatar ? appendImageRepoDomainIfNotExistInUrl(avatar) : null;
  }

  return null;
}

/**
 * Check if user is in thread
 */
export function isUserInThread(thread: Thread, userId: string): boolean {
  return thread.users?.some((user) => user.Id === userId) || false;
}

/**
 * Check if thread is a business thread
 */
export function isBusinessThread(thread: Thread): boolean {
  return thread.threadType === 'BUSINESS' || !!thread.vendor;
}

/**
 * Check if thread is a cognition/AI thread
 */
export function isAIThread(thread: Thread): boolean {
  return thread.threadType === 'COGNITION' || thread.threadType === 'MY_COGNITION';
}

/**
 * Get thread type label
 */
export function getThreadTypeLabel(thread: Thread): string {
  if (isAIThread(thread)) return 'AI';
  if (isBusinessThread(thread)) return 'Business';
  return 'Personal';
}

/**
 * Format last message preview
 */
export function formatLastMessagePreview(thread: Thread): string {
  if (!thread.lastMessage) return '';

  const message = thread.lastMessage;
  if (message.message) {
    return message.message;
  }

  if (message.contents && message.contents.length > 0) {
    const firstContent = message.contents[0];
    if (firstContent.contentType === 'text' && firstContent.content.text) {
      return firstContent.content.text;
    }
    if (firstContent.contentType === 'image') {
      return '📷 Image';
    }
    if (firstContent.contentType === 'video') {
      return '🎥 Video';
    }
    if (firstContent.contentType === 'pdf') {
      return '📄 PDF';
    }
    if (firstContent.contentType === 'link') {
      return '🔗 Link';
    }
  }

  return 'Message';
}

/**
 * Get online status for thread (placeholder for future implementation)
 */
export function getThreadOnlineStatus(thread: Thread): boolean {
  // TODO: Implement real online status based on user presence
  return true;
}
